import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../Sidebar';
import TopBar from '../Dashboard/TopBar';
import axios from 'axios';
import { Trash2, Plus, X, RefreshCw, Hash, User, Eye, Download } from 'lucide-react';
import Order from './Order'; 
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const API = "https://admin.theporch.in/api";
const brandColor = "#ae7d54";

const LiveOrders = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('All');
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Ref for PDF Generation
  const receiptRef = useRef(null);

  // ===============================
  // ✨ DATA CLEANING UTILITY
  // ===============================
  const cleanItemString = (str) => {
    if (!str) return "";
    // Removes backslashes and double quotes like \"Peanut Chatkara (x1)\"
    return str.replace(/\\"/g, '').replace(/"/g, '').trim();
  };

  // ===============================
  // 🚀 FETCH DATA (Filtered by Today)
  // ===============================
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/orders/live`);
      
      const today = new Date().toISOString().split('T')[0];
      const filteredToday = res.data.filter(order => {
        const orderDate = new Date(order.created_at).toISOString().split('T')[0];
        return orderDate === today;
      });

      const sorted = filteredToday.sort((a, b) => b.id - a.id);
      setOrders(sorted);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 20000);
    return () => clearInterval(interval);
  }, []);

  // ===============================
  // 🔄 UPDATE STATUS
  // ===============================
  const updateStatus = async (id, newStatus) => {
    try {
      await axios.put(`${API}/orders/${id}/status`, { status: newStatus });
      fetchData();
    } catch (err) {
      console.error("Status update error:", err);
    }
  };

  // ===============================
  // 🗑 DELETE ORDER
  // ===============================
  const deleteOrder = async (id) => {
    if (!window.confirm("Move this order to history/delete?")) return;
    try {
      await axios.delete(`${API}/orders/${id}`);
      fetchData();
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  // ===============================
  // 📄 DOWNLOAD PDF RECEIPT
  // ===============================
  const downloadReceipt = async (order) => {
    // We set the order as selected temporarily to render it in the DOM for the capture
    setSelectedOrder(order);
    
    // Small timeout to ensure the DOM is rendered before capturing
    setTimeout(async () => {
      const element = document.getElementById('receipt-content');
      if (!element) return;

      try {
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff"
        });
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'px',
          format: [420, 600] // Matching the modal width
        });

        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`Order_${order.table_number}_${order.id}.pdf`);
      } catch (error) {
        console.error("PDF generation failed", error);
      }
    }, 100);
  };

  const getStatusStyle = (status) => {
    const map = {
      Pending: { bg: '#fff7ed', text: '#c2410c' },
      Cooking: { bg: '#eff6ff', text: '#1d4ed8' },
      Ready: { bg: '#dcfce7', text: '#15803d' },
      Cancelled: { bg: '#fef2f2', text: '#dc2626' }
    };
    return map[status] || { bg: '#f1f5f9', text: '#475569' };
  };

  const displayedOrders = orders.filter(order => 
    filterStatus === 'All' ? true : order.status === filterStatus
  );

  return (
    <div className="layout-wrapper" style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc', fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        .main-container { flex: 1; margin-left: 260px; width: calc(100% - 260px); transition: 0.3s ease; }
        .content-wrapper { padding: 40px; max-width: 1800px; margin: 0 auto; }
        .header-section { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
        .filter-container { display: flex; gap: 10px; margin-bottom: 25px; overflow-x: auto; padding-bottom: 5px; }
        .filter-btn { padding: 8px 20px; border-radius: 50px; border: 1px solid #e2e8f0; background: white; color: #64748b; font-weight: 600; cursor: pointer; transition: 0.2s; font-size: 13px; }
        .filter-btn.active { background: ${brandColor}; color: white; border-color: ${brandColor}; }
        .add-btn { background: ${brandColor}; color: white; border: none; padding: 12px 24px; border-radius: 12px; cursor: pointer; font-weight: 600; display: flex; align-items: center; gap: 8px; transition: 0.2s; box-shadow: 0 4px 12px rgba(174, 125, 84, 0.2); }
        .table-card { background: white; border-radius: 24px; border: 1px solid #e2e8f0; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); }
        .custom-table { width: 100%; border-collapse: collapse; }
        .custom-table th { background: #f8fafc; padding: 18px; font-size: 11px; font-weight: 700; text-transform: uppercase; color: #64748b; border-bottom: 1px solid #e2e8f0; text-align: left; }
        .custom-table td { padding: 16px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
        .status-badge { border: none; padding: 8px 12px; border-radius: 10px; font-weight: 700; cursor: pointer; font-size: 12px; outline: none; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 20px; }
        .modal-body { background: white; width: 100%; max-width: 1200px; height: 85vh; border-radius: 24px; overflow: hidden; position: relative; }
        .view-modal-card { background: white; width: 420px; border-radius: 28px; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); }
        @media (max-width: 1024px) { .main-container { margin-left: 0; width: 100%; } }
      `}</style>

      <Sidebar isOpen={isSidebarOpen} />
      
      <main className="main-container">
        <TopBar onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} />
        
        <div className="content-wrapper">
          <div className="header-section">
            <div>
              <h2 style={{ margin: 0, color: '#1e293b' }}>Live Orders</h2>
              <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>Managing today's activities</p>
             </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={fetchData} className="add-btn" style={{ background: '#f1f5f9', color: '#64748b', boxShadow: 'none' }}>
                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              </button>
              <button className="add-btn" onClick={() => setShowOrderModal(true)}>
                <Plus size={18} /> New Order
              </button>
            </div>
          </div>

          <div className="filter-container">
            {['All', 'Pending', 'Cooking', 'Ready', 'Cancelled'].map((status) => (
              <button 
                key={status} 
                className={`filter-btn ${filterStatus === status ? 'active' : ''}`}
                onClick={() => setFilterStatus(status)}
              >
                {status}
              </button>
            ))}
          </div>

          <div className="table-card">
            <table className="custom-table">
              <thead>
                <tr>
                  <th>Table</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedOrders.length === 0 ? (
                  <tr><td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No {filterStatus.toLowerCase()} orders for today</td></tr>
                ) : (
                  displayedOrders.map(order => {
                    const style = getStatusStyle(order.status);
                    return (
                      <tr key={order.id}>
                        <td data-label="Table">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ background: '#fef3c7', color: brandColor, padding: '8px', borderRadius: '8px' }}><Hash size={14} /></div>
                            <span style={{ fontWeight: '800', fontSize: '16px' }}>{order.table_number}</span>
                          </div>
                        </td>
                        <td data-label="Customer">
                          <div style={{ fontWeight: '700' }}>{order.customer_name}</div>
                          <div style={{ fontSize: '12px', color: '#94a3b8' }}>{order.customer_mobile}</div>
                        </td>
                        <td data-label="Items" style={{ fontSize: '14px', color: '#475569', maxWidth: '300px' }}>
                          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {cleanItemString(order.items)}
                          </div>
                        </td>
                        <td data-label="Status">
                          <select
                            value={order.status}
                            onChange={(e) => updateStatus(order.id, e.target.value)}
                            className="status-badge"
                            style={{ background: style.bg, color: style.text }}
                          >
                            <option value="Pending">Pending</option>
                            <option value="Cooking">Cooking</option>
                            <option value="Ready">Ready</option>
                            <option value="Cancelled">Cancelled</option>
                          </select>
                        </td>
                        <td data-label="Actions">
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button 
                              onClick={() => setSelectedOrder(order)}
                              title="View Order"
                              style={{ background: '#f1f5f9', border: 'none', padding: '8px', borderRadius: '8px', cursor: 'pointer' }}
                            >
                              <Eye size={18} color="#64748b" />
                            </button>
                            <button 
                              onClick={() => downloadReceipt(order)}
                              title="Download Receipt"
                              style={{ background: '#ecfdf5', border: 'none', padding: '8px', borderRadius: '8px', cursor: 'pointer' }}
                            >
                              <Download size={18} color="#10b981" />
                            </button>
                            <button 
                              onClick={() => deleteOrder(order.id)}
                              title="Delete Order"
                              style={{ background: '#fef2f2', border: 'none', padding: '8px', borderRadius: '8px', cursor: 'pointer' }}
                            >
                              <Trash2 size={18} color="#dc2626" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* NEW ORDER MODAL */}
      {showOrderModal && (
        <div className="modal-overlay">
          <div className="modal-body">
            <button 
              onClick={() => { setShowOrderModal(false); fetchData(); }}
              style={{ position: 'absolute', top: 20, right: 20, zIndex: 100, background: '#f1f5f9', border: 'none', borderRadius: '50%', padding: '8px', cursor: 'pointer' }}
            >
              <X size={20} />
            </button>
            <Order isModal={true} onOrderSuccess={() => { setShowOrderModal(false); fetchData(); }} />
          </div>
        </div>
      )}

      {/* VIEW / RECEIPT MODAL */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="view-modal-card" id="receipt-content" onClick={e => e.stopPropagation()}>
            {/* Header Area */}
            <div style={{ padding: '25px', background: '#1e293b', color: 'white' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', opacity: 0.8 }}>Order Receipt</span>
                <X size={20} className="no-pdf" style={{ cursor: 'pointer' }} onClick={() => setSelectedOrder(null)} />
              </div>
              <h1 style={{ margin: '10px 0 0 0', fontSize: '32px' }}>Table {selectedOrder.table_number}</h1>
            </div>

            {/* Body Area */}
            <div style={{ padding: '25px' }}>
              <div style={{ display: 'flex', gap: '15px', marginBottom: '25px', padding: '15px', background: '#f8fafc', borderRadius: '15px' }}>
                <div style={{ background: brandColor, color: 'white', width: '45px', height: '45px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <User size={20} />
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 'bold' }}>CUSTOMER</div>
                  <div style={{ fontWeight: '700', fontSize: '16px' }}>{selectedOrder.customer_name}</div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>{selectedOrder.customer_mobile}</div>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 'bold', marginBottom: '10px' }}>ORDER ITEMS</div>
                <div style={{ background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: '15px', overflow: 'hidden' }}>
                  {cleanItemString(selectedOrder.items).split(',').map((item, index, arr) => (
                    <div 
                      key={index} 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        padding: '12px 15px', 
                        borderBottom: index === arr.length - 1 ? 'none' : '1px solid #f1f5f9',
                        gap: '12px'
                      }}
                    >
                      <div style={{ minWidth: '14px', height: '14px', border: '1px solid #22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px' }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e' }}></div>
                      </div>
                      <span style={{ fontSize: '15px', fontWeight: '600', color: '#334155' }}>
                        {item.trim()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', borderTop: '1px dashed #e2e8f0', paddingTop: '20px' }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 'bold' }}>PLACED AT</div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>
                    {new Date(selectedOrder.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 'bold' }}>STATUS</div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: getStatusStyle(selectedOrder.status).text }}>
                    {selectedOrder.status}
                  </div>
                </div>
              </div>
            </div>

            {/* Total Footer */}
            <div style={{ padding: '20px 25px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: '700', color: '#64748b' }}>Total Amount</span>
              <span style={{ fontSize: '24px', fontWeight: '900', color: '#1e293b' }}>₹{selectedOrder.total_amount || "0"}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveOrders;